import {
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography
} from '@material-ui/core';
import api from 'services/api';
import { ANAO, ASIM } from 'consts';
import { getCodEmpresa } from 'lib/functions';
import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    maxWidth: 900
  },
  title: { marginBottom: theme.spacing(2) },
  card: { marginBottom: theme.spacing(2) },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  actions: { marginTop: theme.spacing(3) },
  message: { marginTop: theme.spacing(2) }
}));

const INITIAL_FORM = {
  titularcartaoobg: ANAO,
  nrocartaoobg: ANAO,
  nsucartaoobg: ANAO,
  nroautcartaoobg: ANAO,
  vendeservprivporveic: ANAO,
  usaclienteporpdv: ANAO,
  permitedefpdvdotarifario: ANAO,
  exibepaxnaodefcomonoshow: ANAO,
  permconsosperiodoantpdvmovel: ANAO,
  exibevoucherdigitalpdvmovel: ANAO,
  visqtdediasantes: 0,
  visqtdediasdepois: 0
};

const CAMPOS_DIAS = ['visqtdediasantes', 'visqtdediasdepois'];

const DIAS_MAXIMO = 365;

const ESTILO_DIAS = {
  display: 'flex',
  gap: 16,
  flexWrap: 'wrap',
  marginTop: 12
};

const PROPS_INPUT_DIAS = {
  min: 0,
  max: DIAS_MAXIMO,
  step: 1
};

/* Campos obrigatórios na forma cartão */
const CAMPOS_CARTAO = [
  ['titularcartaoobg', 'Nome titular'],
  ['nrocartaoobg', 'Número cartão'],
  ['nsucartaoobg', 'NSU'],
  ['nroautcartaoobg', 'Autorização']
];

/* Campos do tarifário */
const CAMPOS_TARIFARIO = [
  ['permitedefpdvdotarifario', 'Define PDV do tarifário'],
  ['usaclienteporpdv', 'Usa cliente por PDV'],
  ['exibevoucherdigitalpdvmovel', 'Permitir baixar Voucher Digital no PDV Móvel (requer Sisare Passenger configurado)'],
  ['exibepaxnaodefcomonoshow', 'Exibir pax não definido como show/no-show como no-show no PDV Móvel']
];

/* Verifica se a quantidade de dias é inválida */
const isDiasInvalido = (value) => (
  !Number.isInteger(Number(value)) ||
  value === '' ||
  Number(value) < 0 ||
  Number(value) > DIAS_MAXIMO
);

export default function ConfiguracaoGeral() {
  const classes = useStyles();

  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const codempresa = getCodEmpresa() || 0;
  const isPeriodoDisabled = form.permconsosperiodoantpdvmovel !== ASIM;

  /* Carrega os dados iniciais da tela */
  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const response = await api.get('/pdv/configuracoes/geral', { params: { codempresa } });

        if (active) {
          setForm({
            ...INITIAL_FORM,
            ...(response.data || {})
          });
        }
      } catch (e) {
        if (active) {
          setMessage(e.response?.data?.error || 'Não foi possível carregar as configurações!');
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [codempresa]);

  /* Evento onChange dos checkboxes */
  const toggle = (field) => (event) => {
    const { checked } = event.target;

    setMessage('');

    setForm((old) => ({
      ...old,
      [field]: checked ? ASIM : ANAO
    }));
  };

  /* Evento onChange dos campos de dias */
  const setDias = (field) => (event) => {
    const { value } = event.target;

    setForm((old) => ({
      ...old,
      [field]: value === '' ? '' : Number(value)
    }));
  };

  /* Clicar para salvar */
  const salvar = async () => {
    if (CAMPOS_DIAS.some((field) => isDiasInvalido(form[field]))) {
      setMessage(`Informe dias antes e depois entre 0 e ${DIAS_MAXIMO}.`);

      return;
    }

    setSaving(true);
    setMessage('');

    try {
      await api.put('/pdv/configuracoes/geral', {
        codempresa,
        ...form
      });

      setMessage('Configurações salvas com sucesso!');
    } catch (e) {
      setMessage(e.response?.data?.error || 'Não foi possível salvar as configurações!');
    } finally {
      setSaving(false);
    }
  };

  /* Renderiza o checkbox do campo */
  const renderCheckbox = (field, label) => (
    <FormControlLabel
      key={field}
      label={label}
      control={
        <Checkbox
          checked={form[field] === ASIM}
          onChange={toggle(field)}
          color="primary"
        />
      }
    />
  );

  if (loading) {
    return (
      <div className={classes.root}>
        <CircularProgress size={28} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Typography
        variant="h4"
        className={classes.title}
      >
        Configurações Gerais do PDV Móvel
      </Typography>

      <Card className={classes.card}>
        <CardContent>
          <Typography
            variant="h6"
            className={classes.sectionTitle}
          >
            Campos obrigatórios na forma cartão
          </Typography>

          <FormGroup>
            {CAMPOS_CARTAO.map(([field, label]) => renderCheckbox(field, label))}
          </FormGroup>
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardContent>
          <FormGroup>
            {renderCheckbox('vendeservprivporveic', 'Vende serviço privativo por veículo')}
          </FormGroup>

          <Typography
            variant="h6"
            className={classes.sectionTitle}
            style={{ marginTop: 16 }}
          >
            Tarifário
          </Typography>

          <FormGroup>
            {CAMPOS_TARIFARIO.map(([field, label]) => renderCheckbox(field, label))}
          </FormGroup>
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardContent>
          <Typography
            variant="h6"
            className={classes.sectionTitle}
          >
            Consulta de OS e escalas - período permitido
          </Typography>

          {renderCheckbox(
            'permconsosperiodoantpdvmovel',
            'Permite consultar OS de período diferente da data atual - PDV Móvel'
          )}

          <div style={ESTILO_DIAS}>
            <TextField
              label="Dias antes"
              type="number"
              variant="outlined"
              value={form.visqtdediasantes}
              onChange={setDias('visqtdediasantes')}
              disabled={isPeriodoDisabled}
              inputProps={PROPS_INPUT_DIAS}
            />

            <TextField
              label="Dias depois"
              type="number"
              variant="outlined"
              value={form.visqtdediasdepois}
              onChange={setDias('visqtdediasdepois')}
              disabled={isPeriodoDisabled}
              inputProps={PROPS_INPUT_DIAS}
            />
          </div>
        </CardContent>
      </Card>

      <div className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          onClick={salvar}
          disabled={saving}
        >
          {saving ? 'SALVANDO...' : 'SALVAR'}
        </Button>
      </div>

      {message && (
        <Typography className={classes.message}>
          {message}
        </Typography>
      )}
    </div>
  );
};